import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";

const locationsFaqs: LearnFAQ[] = [
  {
    question: "How many SAINT providers are there?",
    answer:
      "The number grows as Magnus Medical credentials new sites. As of the last time we checked the Magnus Medical provider locator, SAINT® was available in a limited number of states. Check the locator for the current count and the most up-to-date list of Verified SAINT® Providers.",
  },
  {
    question: "Is SAINT available near me?",
    answer:
      "SAINT® is available at a limited number of Verified SAINT® Providers listed on the Magnus Medical provider locator. If no verified provider is in your state, many patients travel — the five-day schedule makes a single trip practical. Neuro Wellness TMS Centers of America in Coral Springs, Florida treats patients from across Florida and from other states.",
  },
  {
    question: "Is SAINT done in a hospital or outpatient?",
    answer:
      "Both settings exist. Some academic medical centers deliver SAINT® in hospital-based programs; private outpatient clinics deliver the same FDA-cleared system in a clinic setting. Neuro Wellness delivers SAINT® in a private outpatient suite — no anesthesia, no overnight stay, and most patients can drive themselves home after each session.",
  },
  {
    question: "How do I know a clinic is a verified SAINT provider?",
    answer:
      "Ask the clinic directly whether they use the Magnus Neuromodulation System with SAINT Technology and whether their targeting is based on a functional MRI scan. Then confirm the clinic appears on the Magnus Medical provider locator. A clinic offering 'accelerated TMS' or a 'Stanford protocol' without naming the Magnus system may not be delivering the FDA-cleared SAINT® protocol.",
  },
  {
    question: "Can I do SAINT if standard TMS didn't work for me?",
    answer:
      "Possibly. SAINT® uses fMRI-guided personalized targeting and a different dosing schedule (ten sessions per day for five days) than standard TMS. Whether it is appropriate depends on your diagnosis, medication history, and screening. A consultation with a psychiatrist determines eligibility; we do not guarantee that prior TMS response predicts SAINT® response. Individual results may vary.",
  },
  {
    question: "Do I need a referral to start SAINT?",
    answer:
      "No referral is required at Neuro Wellness. You can call us directly. Records from your psychiatrist or primary care doctor — especially your medication history — are helpful but not mandatory. We review your history at your free consultation and tell you whether SAINT® is the right next step.",
  },
];

export const pageSaintTmsLocations = makeArticle({
  slug: "saint-tms-locations",
  title:
    "SAINT® TMS Near Me: Verified Providers by State (2026) | Neuro Wellness",
  description:
    "Looking for SAINT® TMS near you? Verified SAINT® providers by state, how to confirm a clinic uses the real SAINT system, and travel options for the 5-day course.",
  canonical: "/learn/saint-tms-locations",
  h1: "SAINT® TMS Near Me: Verified Providers by State (2026)",
  breadcrumbLabel: "SAINT® TMS Near Me",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared, fMRI-guided accelerated TMS protocol available at a limited number of Verified SAINT® Providers nationwide. The authoritative list is maintained on Magnus Medical's provider locator. Neuro Wellness TMS Centers of America is a Verified SAINT® Provider in Coral Springs, Florida, treating adults 22+ who travel from across Florida and from other states for the five-day course. Because the protocol requires the Magnus Neuromodulation System, fMRI targeting, and manufacturer-certified training, it is not available at every TMS clinic.",
  keyTakeaways: [
    "SAINT® is available only at Verified SAINT® Providers credentialed by Magnus Medical — not at every TMS clinic.",
    "The authoritative list of current sites is on the Magnus Medical provider locator.",
    "Two kinds of sites exist: hospital-based programs and private outpatient clinics, both delivering the same FDA-cleared system.",
    "Neuro Wellness in Coral Springs, Florida is a Verified SAINT® Provider about 25 minutes from Fort Lauderdale airport.",
    "Because the course takes five consecutive days, many patients travel once rather than commute for six weeks.",
  ],
  sections: [
    {
      heading: "SAINT\u00ae TMS near me: quick answer",
      paragraphs: [
        "To find SAINT\u00ae TMS near you, check Magnus Medical's official provider locator at magnusmedical.com. Because SAINT\u00ae requires the FDA-cleared Magnus Neuromodulation System, proprietary fMRI-guided targeting software, and manufacturer certification, it is available at a limited number of credentialed sites nationwide.",
        "In South Florida, Neuro Wellness TMS Centers of America in Coral Springs is a Verified SAINT\u00ae Provider serving local patients across Broward, Palm Beach, and Miami-Dade counties \u2014 including [[Miami|/locations/saint-tms-miami]], [[Fort Lauderdale|/locations/saint-tms-fort-lauderdale]], [[Boca Raton|/locations/saint-tms-boca-raton]], [[Coral Gables|/locations/saint-tms-coral-gables]], and [[Coral Springs|/learn/saint-tms-coral-springs-south-florida]] \u2014 as well as out-of-state travelers. If no verified provider is in your state, traveling for the 1-week course is often the fastest path.",
      ],
    },
    {
      heading: "Quick answer: where SAINT® TMS is available",
      paragraphs: [
        "SAINT® is delivered on the Magnus Neuromodulation System with SAINT Technology, which Magnus Medical, Inc. licenses exclusively from Stanford University. A clinic must complete manufacturer-certified training and use the Magnus system with fMRI-guided targeting to be listed as a Verified SAINT® Provider. The current list of verified sites is on the Magnus Medical provider locator at magnusmedical.com/find-a-provider.",
        "Neuro Wellness TMS Centers of America is a Verified SAINT® Provider at 9734 W Sample Rd, 2nd Floor, Suite B, Coral Springs, FL 33065. We treat adults 22 and older with major depressive disorder whose depression has not improved enough on prior antidepressant medication. Most of our SAINT® patients travel to us from other states for the five-day treatment week.",
      ],
      links: [
        {
          to: "/saint",
          text: "Learn more about SAINT® at Neuro Wellness",
        },
        {
          to: "/am-i-a-candidate-screener",
          text: "See if you may be a candidate",
        },
      ],
    },
    {
      heading: "Verified SAINT® providers by state",
      paragraphs: [
        "The table below lists Neuro Wellness TMS Centers of America as a verified Florida site. Additional verified providers across other states can be found on the Magnus Medical provider locator. We do not maintain a separate list of other clinics — the locator is the authoritative source, and it is updated as Magnus credentials new sites.",
      ],
      table: {
        headers: [
          "State",
          "City",
          "Provider",
          "Setting",
          "Verified per Magnus locator (date checked)",
        ],
        rows: [
          [
            "Florida",
            "Coral Springs",
            "Neuro Wellness TMS Centers of America",
            "Private outpatient",
            "October 2026",
          ],
          // Additional rows to be populated from the Magnus Medical provider locator.
          // Do not invent providers — add only verified listings.
        ],
      },
      paragraphsAfter: [
        "Last checked: October 2026. Locations are added over time, so a state that had no listing last year may have one now. Always confirm a clinic's current status on the Magnus Medical provider locator before booking.",
      ],
    },
    {
      heading: "How to verify a clinic uses the actual SAINT® system",
      paragraphs: [
        "Not every clinic that advertises 'accelerated TMS' or a 'five-day protocol' delivers SAINT®. SAINT® is defined by three elements: the Magnus Neuromodulation System, fMRI-guided personalized targeting, and a specific intermittent theta burst schedule of ten sessions per day for five consecutive days. If any of these is missing, the treatment is not SAINT®.",
        "Red flags to watch for: a clinic that says it offers a 'SAINT-like' protocol or a 'Stanford protocol' but does not name the Magnus Neuromodulation System; a clinic that cannot confirm fMRI-based targeting; or a clinic that is not listed on the Magnus Medical provider locator. Any of these is a reason to ask more questions before proceeding.",
      ],
      list: [
        "Ask directly: 'Do you use the Magnus Neuromodulation System with SAINT Technology?'",
        "Confirm that the stimulation target is chosen from your own functional MRI scan, not scalp measurements alone.",
        "Check the Magnus Medical provider locator to verify the clinic is listed.",
        "Ask how many patients the clinic has treated with the SAINT® protocol specifically.",
        "Ask who sets the treatment target and what happens if the treatment does not produce the hoped-for response.",
      ],
      paragraphsAfter: [
        "A reputable provider will answer these questions clearly. If a clinic is vague about the device or the targeting method, that is a reason to look elsewhere.",
      ],
    },
    {
      heading:
        "Traveling for SAINT®: what the week looks like from out of state",
      paragraphs: [
        "Because SAINT® is available at a limited number of sites, many patients travel. The five-day schedule can make travel more practical than a traditional six-week course: instead of commuting to a clinic 30 to 36 times over two months, a patient travels once, stays locally for one week, and completes the entire course.",
        "A typical week: arrive the day before treatment starts; complete five consecutive treatment days (ten sessions per day with roughly 50-minute breaks); and arrange follow-up care with your home clinicians before you leave. A pre-treatment evaluation and MRI must be completed beforehand, which can often be scheduled for the day you arrive or coordinated remotely.",
        "Neuro Wellness is about 25 minutes from Fort Lauderdale-Hollywood International Airport (FLL) and within reach of Palm Beach (PBI) and Miami (MIA) airports. We help traveling patients plan the timing so the scan and treatment week fit into one trip, and we can share recommendations on nearby lodging when you book your consultation.",
      ],
      links: [
        {
          to: "/saint-tms-travel",
          text: "Read our traveling-for-SAINT® guide",
        },
        {
          to: "/learn/saint-tms-cost",
          text: "See SAINT® cost and what's included",
        },
      ],
    },
    {
      heading: "State-specific travel guides",
      paragraphs: [
        "If you're coming from a state with no verified SAINT® provider, we've written dedicated travel guides for several regions. Each covers drive times, airports, lodging notes, and what your consultation looks like from there:",
      ],
      links: [
        {
          to: "/learn/saint-tms-utah",
          text: "SAINT® TMS for Utah Residents",
        },
        {
          to: "/learn/saint-tms-nevada",
          text: "SAINT® TMS for Nevada Residents",
        },
        {
          to: "/learn/saint-tms-colorado",
          text: "SAINT® TMS for Colorado Residents",
        },
      ],
    },
    {
      heading: "Florida and the Southeast: Neuro Wellness, Coral Springs",
      paragraphs: [
        "Neuro Wellness TMS Centers of America is a Verified SAINT® Provider in Coral Springs, Florida, serving Broward, Miami-Dade, and Palm Beach counties. The clinic offers SAINT® on the Magnus Neuromodulation System, along with standard TMS, SPRAVATO® (esketamine), IV ketamine infusions, ketamine-assisted psychotherapy, psychotherapy, and medication management.",
        "Self-pay patients typically move from consultation to treatment within a few days. SAINT® at NWCOA starts at $25,000; financing is available on a case-by-case basis and discussed during your consultation. SAINT® may be covered; coverage varies by plan, and we verify your benefits for free, whatever carrier you have.",
        "Appointments are available seven days a week in private treatment suites with extended care-team hours. Most SAINT® patients travel from out of state, and many bring a family member or friend for the week.",
      ],
      links: [
        {
          to: "/check-insurance-options",
          text: "Check my insurance benefits — free",
        },
        {
          to: "/saint",
          text: "Learn more about SAINT® at Neuro Wellness",
        },
      ],
    },
    {
      heading: "What to do next",
      paragraphs: [
        "The right location is the one that can see you soon, treat you safely, and give you a clear answer about whether SAINT® fits your history. At Neuro Wellness, your first appointment is an evaluation, not a commitment. We review your medication history and current symptoms and tell you whether SAINT® is the right next step — and how quickly we can get you in.",
        "If you are traveling, we help plan the timing of your scan and treatment week so it is one trip, not several. Call 954-827-9793 or request a consultation online.",
      ],
      links: [
        {
          to: "/am-i-a-candidate-screener",
          text: "Learn if SAINT® is a fit — free",
        },
        {
          to: "/check-insurance-options",
          text: "Check my insurance",
        },
        {
          to: "/contactus",
          text: "Schedule a free consultation",
        },
      ],
    },
  ],
  faqs: locationsFaqs,
  sources: [
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Magnus Medical. SAINT® Technology and Verified Provider locator.",
      url: "https://magnusmedical.com",
    },
    {
      citation:
        "Stanford Medicine. Stanford Brain Stimulation Lab — SAINT/SNT research.",
      url: "https://med.stanford.edu/brainstimulation",
    },
  ],
  about: [
    {
      "@type": "MedicalTherapy",
      name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
      description:
        "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System with SAINT Technology (FDA 510(k) K220177): 10 sessions per day for 5 consecutive days.",
    },
  ],
  relatedLinks: [
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "What Is SAINT® TMS?",
      href: "/learn/what-is-saint-tms",
      description: "FDA clearance, protocol, and how SAINT differs from TMS",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "What SAINT® costs and how to plan for payment",
    },
    {
      title: "SAINT® vs fMRI-Guided Accelerated TMS",
      href: "/learn/saint-vs-accelerated-tms",
      description:
        "What's the same, what isn't, and what the FDA clearance covers",
    },
    {
      title: "Traveling for SAINT®",
      href: "/saint-tms-travel",
      description:
        "Planning your trip to Coral Springs for the five-day course",
    },
    {
      title: "Does Insurance Cover SAINT®?",
      href: "/learn/does-insurance-cover-saint-tms",
      description: "Coverage, prior authorization, and benefits verification",
    },
    {
      title: "Schedule a Consultation",
      href: "/contactus",
      description: "Book a free consultation with our clinical team",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    {
      label: "Quick answer",
      href: "#quick-answer-where-saint-tms-is-available",
    },
    {
      label: "Verified providers by state",
      href: "#verified-saint-providers-by-state",
    },
    {
      label: "How to verify a clinic",
      href: "#how-to-verify-a-clinic-uses-the-actual-saint-system",
    },
    {
      label: "Traveling for SAINT®",
      href: "#traveling-for-saint-what-the-week-looks-like-from-out-of-state",
    },
    {
      label: "Florida and the Southeast",
      href: "#florida-and-the-southeast-neuro-wellness-coral-springs",
    },
    { label: "What to do next", href: "#what-to-do-next" },
    { label: "FAQ", href: "#faq" },
    { label: "Sources", href: "#sources" },
    { label: "Safety", href: "#safety" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
